import prefixData from '../assets/json/prefix.json';

export interface PhoneValidationResult {
  isValid: boolean;
  operator?: string;
  error?: string;
  formattedNumber?: string;
}

export interface OperatorPrefix {
  operator: string;
  prefixNumbers: string;
  length: string;
}

/**
 * Validates phone number input to only allow English digits
 * @param input - The input string to validate
 * @returns boolean indicating if input contains only English digits
 */
export function isEnglishDigitsOnly(input: string): boolean {
  return /^[0-9]*$/.test(input);
}

/**
 * Validates Myanmar phone numbers against a specific operator
 * @param phoneNumber - The phone number to validate (with or without country code)
 * @param selectedOperator - The operator to validate against (optional)
 * @returns PhoneValidationResult object with validation details
 */
export function validateMyanmarPhoneNumber(
  phoneNumber: string,
  selectedOperator?: string,
): PhoneValidationResult {
  // Check if phone number contains only English digits
  const digitsOnly = phoneNumber.replace(/[\s\-()+ ]/g, '');
  if (!isEnglishDigitsOnly(digitsOnly)) {
    return {
      isValid: false,
      error: 'Please use English digits only',
    };
  }

  return validateMyanmarPhoneNumberInternal(phoneNumber, selectedOperator);
}

/**
 * Internal validation function for Myanmar phone numbers
 * @param phoneNumber - The phone number to validate (with or without country code)
 * @param selectedOperator - The operator to validate against (optional)
 * @returns PhoneValidationResult object with validation details
 */
function validateMyanmarPhoneNumberInternal(
  phoneNumber: string,
  selectedOperator?: string,
): PhoneValidationResult {
  if (!phoneNumber) {
    return {
      isValid: false,
      error: 'Phone number is required',
    };
  }

  // Clean the phone number (remove spaces, dashes, etc.)
  let cleanNumber = phoneNumber.replace(/[\s\-()+ ]/g, '');

  // Remove country code if present
  if (cleanNumber.startsWith('95')) {
    cleanNumber = cleanNumber.substring(2);
  } else if (cleanNumber.startsWith('+95')) {
    cleanNumber = cleanNumber.substring(3);
  }

  // Ensure it starts with 0
  if (!cleanNumber.startsWith('0')) {
    cleanNumber = '0' + cleanNumber;
  }

  // Check if the number is too short
  if (cleanNumber.length < 9) {
    return {
      isValid: false,
      error: 'Phone number is too short',
    };
  }

  // Check if the number is too long
  if (cleanNumber.length > 11) {
    return {
      isValid: false,
      error: 'Phone number is too long',
    };
  }

  // Find matching operator prefix
  const prefixes = prefixData as OperatorPrefix[];

  // If a specific operator is selected, only check prefixes for that operator
  const operatorPrefixes = selectedOperator
    ? prefixes.filter((prefix) => prefix.operator === selectedOperator)
    : prefixes;

  for (const prefix of operatorPrefixes) {
    const prefixNumber = prefix.prefixNumbers;
    const expectedLength = parseInt(prefix.length);

    if (cleanNumber.startsWith(prefixNumber)) {
      if (cleanNumber.length === expectedLength) {
        return {
          isValid: true,
          operator: prefix.operator,
          formattedNumber: cleanNumber,
        };
      } else {
        return {
          isValid: false,
          operator: prefix.operator,
          error: `${prefix.operator} numbers should be ${expectedLength} digits long`,
        };
      }
    }
  }

  if (selectedOperator) {
    return {
      isValid: false,
      error: `Phone number does not match ${String(selectedOperator)} format`,
    };
  }

  return {
    isValid: false,
    error: 'Enter a valid Myanmar phone number.',
  };
}

/**
 * Formats a phone number for display (adds spaces for readability)
 * @param phoneNumber - The phone number to format
 * @returns Formatted phone number string
 */
export function formatPhoneNumberDisplay(phoneNumber: string): string {
  const cleaned = phoneNumber.replace(/[\s\-()+ ]/g, '');

  if (cleaned.length === 0) return '';

  let formatted = '';

  // Add first digit
  if (cleaned.length >= 1) {
    formatted += cleaned.slice(0, 1);

    // Add space after first digit
    if (cleaned.length > 1) {
      formatted += ' ';

      // Add next 3 digits
      formatted += cleaned.slice(1, Math.min(4, cleaned.length));

      // Add space after next 3 digits
      if (cleaned.length > 4) {
        formatted += ' ';
        formatted += cleaned.slice(4, cleaned.length);
      }
    }
  }

  return formatted;
}

/**
 * Gets the operator name for a given phone number
 * @param phoneNumber - The phone number to check
 * @returns Operator name or null if not found
 */
export function getOperatorName(phoneNumber: string): string | null {
  const validation = validateMyanmarPhoneNumber(phoneNumber);
  return validation.operator || null;
}

/**
 * Gets all available operators from the prefix data
 * @returns Array of unique operator names
 */
export function getAvailableOperators(): string[] {
  const prefixes = prefixData as OperatorPrefix[];
  const operators = [...new Set(prefixes.map((prefix) => prefix.operator))];
  return operators;
}

/**
 * Gets all prefixes for a specific operator
 * @param operatorName - The operator name to get prefixes for
 * @returns Array of prefix information for the operator
 */
export function getOperatorPrefixes(operatorName: string): OperatorPrefix[] {
  const prefixes = prefixData as OperatorPrefix[];
  return prefixes.filter((prefix) => prefix.operator === operatorName);
}

/**
 * Converts a local phone number to international format
 * @param phoneNumber - The local phone number
 * @returns International format phone number (with +95 prefix)
 */
export function toInternationalFormat(phoneNumber: string): string {
  const validation = validateMyanmarPhoneNumber(phoneNumber);

  if (!validation.isValid || !validation.formattedNumber) {
    return phoneNumber; // Return original if invalid
  }

  // Remove leading 0 and add +95
  const withoutZero = validation.formattedNumber.substring(1);
  return `+95${withoutZero}`;
}

/**
 * Converts a local phone number to API format (95 prefix without +)
 * @param phoneNumber - The local phone number
 * @returns API format phone number (with 95 prefix)
 */
export function toApiFormat(phoneNumber: string): string {
  const validation = validateMyanmarPhoneNumber(phoneNumber);

  if (!validation.isValid || !validation.formattedNumber) {
    return phoneNumber; // Return original if invalid
  }

  // Remove leading 0 and add 95
  const withoutZero = validation.formattedNumber.substring(1);
  return `95${withoutZero}`;
}
